/**
 * ChatSidebar — painel lateral de chat com agente de IA via SSE streaming.
 *
 * Conecta-se ao endpoint POST /chat/stream do backend e renderiza tokens
 * em tempo real. Suporta tool use com cards visuais de progresso.
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Bot, Send, Trash2, X, CheckCircle2 } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import UserContext from '../../contexts/UserContext'
import {
  EmptyState,
  HeaderActions,
  IconButton,
  InputArea,
  MessageBubble,
  MessageRow,
  MessagesArea,
  SendButton,
  SidebarHeader,
  SidebarPanel,
  SidebarWrapper,
  Spinner,
  StreamingCursor,
  SuggestionChip,
  TextInput,
  ToolCard,
} from './style'

// ── Tipos ──────────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ToolEvent {
  id: string           // tool name usado como chave
  tool: string
  label: string
  result?: string
  status: 'running' | 'done'
}

interface StreamEvent {
  type: 'token' | 'tool_start' | 'tool_end' | 'done' | 'error'
  content?: string
  tool?: string
  label?: string
  result?: string
  message?: string
}

interface ChatSidebarProps {
  isOpen: boolean
  onClose: () => void
}

// ── Utilitário: determina o nome da página pelo pathname ──────────────────────

function pageFromPath(pathname: string): string {
  const segments = pathname.replace(/^\/panel\/?/, '').split('/')
  return segments[0] || 'home'
}

// ── Sugestões iniciais ────────────────────────────────────────────────────────

const SUGGESTIONS = [
  'Quais funis existem na plataforma?',
  'Tem algum card atrasado?',
  'Quantos cards há em cada etapa do funil atual?',
  'Cria um card de teste no funil de vendas',
]

// ── Componente ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ordeno_chat_history'
const CHAT_URL = process.env.REACT_APP_CHAT_BASE_URL || 'https://ordeno-operacional-hjk3p7rq3q-rj.a.run.app'

const ChatSidebar: React.FC<ChatSidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useContext(UserContext)
  const location = useLocation()

  // Restaura histórico do localStorage
  const [history, setHistory] = useState<ChatMessage[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  })

  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [toolEvents, setToolEvents] = useState<ToolEvent[]>([])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Persiste histórico
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-60)))
    } catch {
      /* storage cheio */
    }
  }, [history])

  // Scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [history, streamingText, toolEvents])

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input.trim())
    }
  }

  const clearHistory = () => {
    if (isStreaming) {
      abortRef.current?.abort()
      setIsStreaming(false)
      setStreamingText('')
      setToolEvents([])
    }
    setHistory([])
    localStorage.removeItem(STORAGE_KEY)
  }

  // ── Envio da mensagem via SSE ─────────────────────────────────────────────

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text || isStreaming) return

      setInput('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }

      const userMsg: ChatMessage = { role: 'user', content: text }
      const historySnapshot = history.slice(-18) // envia as últimas 18 antes da nova mensagem
      const newHistory = [...history, userMsg]
      setHistory(newHistory)
      setIsStreaming(true)
      setStreamingText('')
      setToolEvents([])

      const ctrl = new AbortController()
      abortRef.current = ctrl

      const userId = user?.sub || (user?.id ? parseInt(user.id, 10) : 1)

      try {
        const resp = await fetch(`${CHAT_URL}/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
          body: JSON.stringify({
            message: text,
            context: {
              page: pageFromPath(location.pathname),
              tenantId: user?.tenantId ?? 1,
              userId,
              userName: user?.name ?? 'Usuário',
              tenantName: '',
            },
            history: historySnapshot,
          }),
          signal: ctrl.signal,
        })

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`)
        }

        const reader = resp.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let accText = ''

        // lê o stream
        let reading = true
        while (reading) {
          const { done, value } = await reader.read()
          if (done) { reading = false; break }

          buffer += decoder.decode(value, { stream: true })

          // processa cada linha SSE
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? '' // última linha pode estar incompleta

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const raw = line.slice(6).trim()
            if (!raw) continue

            let ev: StreamEvent
            try {
              ev = JSON.parse(raw)
            } catch {
              continue
            }

            if (ev.type === 'token' && ev.content) {
              accText += ev.content
              setStreamingText(accText)

            } else if (ev.type === 'tool_start' && ev.tool) {
              setToolEvents((prev) => [
                ...prev,
                { id: `${ev.tool}-${Date.now()}`, tool: ev.tool!, label: ev.label || ev.tool!, status: 'running' },
              ])

            } else if (ev.type === 'tool_end' && ev.tool) {
              setToolEvents((prev) =>
                prev.map((t) =>
                  t.tool === ev.tool && t.status === 'running'
                    ? { ...t, status: 'done', result: ev.result }
                    : t,
                ),
              )
              // zera texto acumulado para a próxima rodada de tokens
              accText = ''
              setStreamingText('')

            } else if (ev.type === 'done') {
              // finaliza
              const finalText = accText.trim()
              if (finalText) {
                setHistory((prev) => [...prev, { role: 'assistant', content: finalText }])
              }
              setStreamingText('')
              setIsStreaming(false)
              setToolEvents([])

            } else if (ev.type === 'error') {
              setHistory((prev) => [
                ...prev,
                { role: 'assistant', content: `Erro: ${ev.message || 'algo deu errado.'}` },
              ])
              setStreamingText('')
              setIsStreaming(false)
              setToolEvents([])
            }
          }
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return
        setHistory((prev) => [
          ...prev,
          { role: 'assistant', content: 'Erro de conexão. Verifique o servidor.' },
        ])
        setStreamingText('')
        setIsStreaming(false)
        setToolEvents([])
      }
    },
    [history, isStreaming, user, location.pathname],
  )

  const handleSuggestion = (s: string) => sendMessage(s)

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <SidebarWrapper $open={isOpen}>
        {isOpen && (
          <SidebarPanel>
          {/* ── Header ── */}
          <SidebarHeader>
            <h3>
              <Bot size={16} color="#0090D3" />
              Assistente Ordeno
            </h3>
            <HeaderActions>
              <IconButton onClick={clearHistory} title="Limpar conversa">
                <Trash2 size={15} />
              </IconButton>
              <IconButton onClick={onClose} title="Fechar">
                <X size={15} />
              </IconButton>
            </HeaderActions>
          </SidebarHeader>

          {/* ── Mensagens ── */}
          <MessagesArea>
            {history.length === 0 && !isStreaming ? (
              <EmptyState>
                <Bot size={36} />
                <p>
                  Olá, <strong>{user?.name?.split(' ')[0] || 'usuário'}</strong>!<br />
                  Como posso ajudar hoje?
                </p>
                <div className="suggestions">
                  {SUGGESTIONS.map((s) => (
                    <SuggestionChip key={s} onClick={() => handleSuggestion(s)}>
                      {s}
                    </SuggestionChip>
                  ))}
                </div>
              </EmptyState>
            ) : (
              <>
                {history.map((msg, i) => (
                  <MessageRow key={i} $role={msg.role}>
                    <MessageBubble $role={msg.role}>{msg.content}</MessageBubble>
                  </MessageRow>
                ))}

                {/* Tool events durante streaming */}
                {toolEvents.map((te) => (
                  <ToolCard key={te.id} $status={te.status}>
                    <span className="tool-icon">
                      {te.status === 'running' ? (
                        <Spinner />
                      ) : (
                        <CheckCircle2 size={12} color="#52c41a" />
                      )}
                    </span>
                    <div className="tool-body">
                      <div className="tool-label">
                        {te.status === 'running' ? te.label : te.tool}
                      </div>
                      {te.result && (
                        <div className="tool-result">{te.result}</div>
                      )}
                    </div>
                  </ToolCard>
                ))}

                {/* Texto sendo transmitido */}
                {streamingText && (
                  <MessageRow $role="assistant">
                    <MessageBubble $role="assistant">
                      {streamingText}
                      <StreamingCursor />
                    </MessageBubble>
                  </MessageRow>
                )}

                {/* Indicador de carregamento (só tools, sem texto ainda) */}
                {isStreaming && !streamingText && toolEvents.length === 0 && (
                  <MessageRow $role="assistant">
                    <MessageBubble $role="assistant">
                      <Spinner />
                    </MessageBubble>
                  </MessageRow>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>

          {/* ── Input ── */}
          <InputArea>
            <TextInput
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Digite uma mensagem…"
              rows={1}
              disabled={isStreaming}
            />
            <SendButton
              $active={!!input.trim() && !isStreaming}
              onClick={() => sendMessage(input.trim())}
              disabled={!input.trim() || isStreaming}
              title="Enviar (Enter)"
            >
              <Send size={15} />
            </SendButton>
          </InputArea>
          </SidebarPanel>
        )}
      </SidebarWrapper>
    </>
  )
}

export default ChatSidebar
